import type { TFunction } from 'i18next'

import {
  userEmailTaken,
  userLastSuperAdmin,
  userSelfDeactivation,
  userSelfDemotion,
} from '@/features/settings/api/useUsers'
import { ApiError } from '@/lib/api'
import { fieldErrorKey, fieldErrors } from '@/lib/problems'
import { refusalMessage } from '@/lib/refusals'

/* How the account screen words a refused write (S-7, O7 and O8).
 *
 * Two refusals are the authority rule's own:
 *
 *   403 `forbidden` with `held_by: super-admin` — the account OUTRANKS the
 *     caller (a superuser, or capabilities or row scopes the caller lacks), on
 *     an edit, a password reset or a (de)activation. The generic forbidden
 *     sentence («Раздел недоступен для вашей роли») is wrong here: the caller
 *     holds the section, just not this person;
 *   422 `validation-failed` with `exceeds-authority` or `reserved` under
 *     `role` / `capabilities` — worded under the field by `fieldErrorKey`.
 *
 * Four more are 409 `conflict` refusals named by their `detail` token (S-13,
 * the http-api.md Conflict contract; predicates in `api/useUsers.ts`):
 * `email-taken`, `self-demotion`, `self-deactivation` and `last-super-admin`.
 * Each gets its own sentence — the generic conflict sentence said that
 * something clashed and not what.
 *
 * Everything else goes through `refusalMessage`, like every other sheet. */

/** The role the server names when an account outranks the caller. */
const OUTRANKED_BY = 'super-admin'

/** Whether a refusal says the account outranks the caller. */
export function isOutranked(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    error.code === 'forbidden' &&
    error.problem.held_by === OUTRANKED_BY
  )
}

/** The sentence for a refused account write, for a Banner. */
export function userRefusalMessage(error: unknown, t: TFunction): string {
  if (isOutranked(error)) return t('directories.users.outrankedRefusal')
  if (userEmailTaken(error)) return t('directories.users.refusals.emailTaken')
  if (userSelfDemotion(error)) return t('directories.users.refusals.selfDemotion')
  if (userSelfDeactivation(error)) return t('directories.users.refusals.selfDeactivation')
  if (userLastSuperAdmin(error)) return t('directories.users.refusals.lastSuperAdmin')
  return refusalMessage(error, t)
}

/** The worded field error under one of the account form's fields, if any. */
export function userFieldError(
  error: unknown,
  field: 'role' | 'capabilities',
  t: TFunction,
): string | undefined {
  const code = fieldErrors(error)[field]?.[0]
  return code ? t(fieldErrorKey(code)) : undefined
}
